import React, {
  createContext,
  useState,
  useEffect,
  useCallback,
  useContext,
  ReactNode,
} from "react";
import api from "../../api/api"; // Axios instance for API calls
import { PaginationInfo } from "../../types/api";

interface ServicesContextType {
  services: any[];
  pagination: PaginationInfo | null;
  loading: boolean;
  fetchServices: (page?: number, perPage?: number) => Promise<void>;
  addService: (service: any) => Promise<void>;
  editService: (service: any) => Promise<void>;
  deleteService: (id: number | string) => Promise<void>;
}

export const ServicesContext = createContext<ServicesContextType | undefined>(
  undefined,
);

interface ServicesProviderProps {
  children: ReactNode;
}

export const useServicesContext = (): ServicesContextType => {
  const context = useContext(ServicesContext);
  if (!context) {
    throw new Error("useServicesContext must be used within ServicesProvider");
  }
  return context;
};

const ServicesProvider: React.FC<ServicesProviderProps> = ({ children }) => {
  const [services, setServices] = useState<any[]>([]);
  const [pagination, setPagination] = useState<PaginationInfo | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const fetchServices = useCallback(async (page: number = 1, perPage: number = 15) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page: page.toString(),
        per_page: perPage.toString(),
      });
      const response = await api.get(`/services?${params.toString()}`);
      
      // Handle paginated response structure: { success: true, data: { data: [...], pagination: {...} } }
      let servicesData = [];
      let paginationData = null;
      const responseData = response.data;
      
      if (responseData?.success) {
        if (responseData.data?.data && Array.isArray(responseData.data.data)) {
          servicesData = responseData.data.data;
          paginationData = responseData.data.pagination || responseData.pagination;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          servicesData = responseData.data;
          paginationData = responseData.pagination;
        } else if (Array.isArray(responseData)) {
          servicesData = responseData;
        }
      } else if (Array.isArray(responseData?.data)) {
        servicesData = responseData.data;
      } else if (Array.isArray(responseData)) {
        servicesData = responseData;
      }
      
      const normalizedData = servicesData.map((service: any) => ({
        ...service,
        isActive: Boolean(service.is_active ?? service.isActive),
        is_active: Boolean(service.is_active ?? service.isActive),
      }));
      setServices(normalizedData);
      setPagination(paginationData);
    } catch (error) {
      console.error("Failed to fetch services:", error);
      setServices([]);
      setPagination(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const addService = async (service: any): Promise<void> => {
    try {
      const response = await api.post("/services", service);
      const newService = response.data.data || response.data;
      const normalizedService = {
        ...newService,
        isActive: Boolean(newService.is_active ?? newService.isActive),
        is_active: Boolean(newService.is_active ?? newService.isActive),
      };
      setServices((prev) => [...prev, normalizedService]);
    } catch (error) {
      console.error("Failed to add service:", error);
      throw error;
    }
  };

  const editService = async (service: any): Promise<void> => {
    try {
      const isActive = service.is_active ?? service.isActive;
      const payload = {
        ...service,
        isActive: isActive,
        is_active: isActive,
      };
      const response = await api.put(`/services/${service.id}`, payload);
      const updatedService = response.data.data || response.data;
      const normalizedService = {
        ...updatedService,
        isActive: Boolean(updatedService.is_active ?? updatedService.isActive),
        is_active: Boolean(updatedService.is_active ?? updatedService.isActive),
      };
      setServices((prev) =>
        prev.map((item) =>
          item.id === service.id ? normalizedService : item
        )
      );
    } catch (error) {
      console.error("Failed to edit service:", error);
      throw error;
    }
  };

  const deleteService = async (id: number | string): Promise<void> => {
    try {
      await api.delete(`/services/${id}`);
      setServices((prev) => prev.filter((item) => item.id !== id));
    } catch (error) {
      console.error("Failed to delete service:", error);
      throw error;
    }
  };

  useEffect(() => {
    fetchServices(1, 15);
  }, [fetchServices]);

  return (
    <ServicesContext.Provider
      value={{
        services,
        pagination,
        loading,
        fetchServices,
        addService,
        editService,
        deleteService,
      }}
    >
      {children}
    </ServicesContext.Provider>
  );
};

export default ServicesProvider;
